import _ from "lodash";
import { defineComponent, markRaw } from 'vue';
import pcm from '@/global-install/global-pcm';

function splitCompPropsAndData(componentOptions, inputData) {
  const propsKeys = Array.isArray(componentOptions.props)
    ? componentOptions.props
    : Object.keys(componentOptions.props || {});

  return _.reduce(_.keys(inputData), (pre, key) => {
    if (propsKeys.includes(key)) {
      pre.props[key] = inputData[key];
    } else {
      pre.data[key] = inputData[key];
    }

    return pre;
  }, {
    props: {},
    data: {}
  })
}

class SimpleHashRouter {
  lastPathName = location.pathname;
  lastSearch = location.search;
  lastHash = location.hash;
  nextHash = '';

  get nextUrl() {
    return this.lastPathName + this.lastSearch + this.nextHash;
  }

  push(hash) {
    this.lastPathName = location.pathname;
    this.lastSearch = location.search;
    this.lastHash = location.hash;
    if(hash) {
      this.nextHash = '#' + hash.replace(/^#/, '');
    }
    history.replaceState(null, '', this.nextUrl);
    return this.nextHash;
  }
  pop() {
    this.nextHash = this.lastHash;
    history.replaceState(null, '', this.nextUrl);
    return this.nextHash;
  }
}

const hashRouter = new SimpleHashRouter();

export class ServiceConfirm {
  confirmComponent = null;
  confirmOpt = null;
  name = '';
  hashName = '';
  previousActiveElement = null;
  focusRafId = undefined;

  constructor(confirmOpt) {
    this.name = confirmOpt.name || `dialog-${Date.now()}`;
    // Wrap the component with markRaw to prevent Vue from making it reactive
    this.confirmOpt = markRaw(confirmOpt);
  }

  popHashRouter() {
    if(location.hash === this.hashName) {
      hashRouter.pop();
    }
  }

  closeConfirm() {
    if(this.confirmComponent?.instance?.visible) {
      this.destroy();
    }
  }

  async confirm(data = {}) {
    if(!this.confirmOpt) {
      return Promise.reject();
    }

    // Save the currently focused element for restoration after dialog closes
    this.previousActiveElement = document.activeElement;

    const { props, data: realData } = splitCompPropsAndData(this.confirmOpt, data);

    if(!this.confirmComponent) {
      // Use PCM to render the component
      this.confirmComponent = pcm.render(this.confirmOpt, props);
    } else {
      // Update props using PCM's update method
      this.confirmComponent.update(props);
    }

    this.hashName = hashRouter.push(this.name);

    return new Promise((resolve, reject) => {
      // Wait for the component to be mounted
      const waitForInstance = () => {
        if (this.confirmComponent.instance) {
          const instance = this.confirmComponent.instance;

          // Store original handlers
          const _handleSure = instance.handleSure?.bind(instance);
          const _handleCancel = instance.handleCancel?.bind(instance);

          // Set data properties
          Object.keys(realData).forEach((key) => {
            if (instance[key] !== undefined) {
              instance[key] = realData[key];
            }
          });

          // Call handleOpen if exists
          if (instance.handleOpen) {
            instance.handleOpen();
          }

          // Set visible to true
          if (instance.visible !== undefined) {
            instance.visible = true;
          }

          // Override handlers
          if (instance.handleSure !== undefined) {
            instance.handleSure = async (args) => {
              if (_handleSure) {
                await _handleSure(args);
              }
              resolve(args);
              this.closeConfirm();
            };
          }

          if (instance.handleCancel !== undefined) {
            instance.handleCancel = async (args) => {
              if (_handleCancel) {
                await _handleCancel(args);
              }
              reject(args);
              this.closeConfirm();
            };
          }
        } else {
          // If instance is not ready yet, wait a bit and try again
          setTimeout(waitForInstance, 20);
        }
      };

      waitForInstance();
    });
  }

  destroy() {
    if (this.confirmComponent) {
      this.popHashRouter();
      this.confirmComponent.destroy?.();
      this.confirmComponent = null;

      // Restore focus to the element that opened the dialog
      this.restoreFocus();
    }
  }

  restoreFocus() {
    const targetElement = this.previousActiveElement;

    if (!targetElement) return;

    // Cancel any existing animation frame to prevent memory leaks
    if (this.focusRafId !== undefined) {
      cancelAnimationFrame(this.focusRafId);
    }

    // Use requestAnimationFrame to delay focus restoration
    // This ensures the dialog close animation completes first
    this.focusRafId = requestAnimationFrame(() => {
      try {
        // Check if element still exists and is focusable
        if (targetElement &&
            typeof targetElement.focus === 'function' &&
            document.body.contains(targetElement)) {
          targetElement.focus();
        }
      } catch (error) {
        console.warn('Failed to restore focus:', error);
      } finally {
        // Clean up
        this.previousActiveElement = null;
        this.focusRafId = undefined;
      }
    });
  }
}

export const ConfirmComponent = defineComponent({
  // Vue3 migration: Using defineComponent for better type inference
  data() {
    return {
      visible: false,
    };
  },
  methods: {
    handleOpen() {},
    handleSure() {},
    handleCancel() {},
  },
});
